/**
 * Regras de realização de atendimento / triagem (Central Amparo).
 * A tolerância após o scheduled_at vem do parâmetro agendamento_tolerancia_minutos.
 * A antecedência antes do horário vem de atendimento_inicio_antecedencia_minutos.
 */

import { DEFAULT_SCHEDULING_LIMITS } from '../types/supportCenterParameters'

/**
 * false = atendimento só no horário agendado, com a tolerância do parâmetro.
 * true libera iniciar e concluir fora do horário. A triagem não usa este interruptor.
 */
export const ATTENDANCE_TIME_WINDOW_DISABLED = false

export function attendancePastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível realizar atendimento de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

export function attendanceEarlyMessage(earlyStartMinutes = 0): string {
  const m = Math.max(0, earlyStartMinutes)
  if (m > 0) {
    return `O atendimento só fica disponível a partir de ${m} minuto${m === 1 ? '' : 's'} antes do horário agendado.`
  }
  return 'O atendimento só fica disponível a partir do horário agendado.'
}

export function attendanceFormPastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível iniciar o atendimento de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

export function attendanceFormEarlyMessage(earlyStartMinutes = 0): string {
  const m = Math.max(0, earlyStartMinutes)
  if (m > 0) {
    return `O início do atendimento só fica disponível a partir de ${m} minuto${m === 1 ? '' : 's'} antes do horário agendado.`
  }
  return 'O início do atendimento só fica disponível a partir do horário agendado.'
}

export function callLinkPastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível abrir o link de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

export function triagePastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível realizar triagem de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

function parseScheduledAt(value: string): Date | null {
  if (!value) return null
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * true se ainda não chegou a janela de início do atendimento
 * (horário agendado menos earlyStartMinutes).
 */
export function isAppointmentBeforeScheduled(
  scheduledAt: string,
  earlyStartMinutes: number = DEFAULT_SCHEDULING_LIMITS.earlyStartMinutes,
  now: Date = new Date()
): boolean {
  const when = parseScheduledAt(scheduledAt)
  if (!when) return true
  const earlyMs = Math.max(0, earlyStartMinutes) * 60 * 1000
  return now.getTime() < when.getTime() - earlyMs
}

/** true se já passou o horário + tolerância em minutos. */
export function isAppointmentPastForAttendance(
  scheduledAt: string,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  now: Date = new Date()
): boolean {
  const when = parseScheduledAt(scheduledAt)
  if (!when) return true
  const toleranceMs = Math.max(0, toleranceMinutes) * 60 * 1000
  return now.getTime() > when.getTime() + toleranceMs
}

type AttendanceEventRef = {
  status: string
  triage_status?: string | null
  scheduled_at: string
  /** Se preenchido, libera link e formulário fora da janela de horário/tolerância. */
  attendance_started_at?: string | null
}

function hasAttendanceStarted(
  event: Pick<AttendanceEventRef, 'attendance_started_at'> | null | undefined
): boolean {
  return Boolean(event?.attendance_started_at?.trim())
}

/**
 * Status em que o atendimento/formulário ainda pode ocorrer.
 * `reagendado` = agendamento antigo substituído; atendimento vai no novo (`marcado`).
 */
function isAttendanceEligibleStatus(status: string): boolean {
  return status === 'marcado' || status === 'realizado'
}

export function attendanceRescheduledMessage(): string {
  return 'Este agendamento foi reagendado. O atendimento deve ser feito no novo horário.'
}

export function canPerformAttendance(
  event: AttendanceEventRef | null | undefined,
  canUpdate: boolean,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  earlyStartMinutes: number = DEFAULT_SCHEDULING_LIMITS.earlyStartMinutes,
  now: Date = new Date()
): boolean {
  if (!canUpdate || !event) return false
  if (!isAttendanceEligibleStatus(event.status)) return false
  if (event.triage_status !== 'concluida') return false
  if (ATTENDANCE_TIME_WINDOW_DISABLED || hasAttendanceStarted(event)) return true
  if (isAppointmentBeforeScheduled(event.scheduled_at, earlyStartMinutes, now)) return false
  if (isAppointmentPastForAttendance(event.scheduled_at, toleranceMinutes, now)) return false
  return true
}

/** Link: disponível antes do horário; após tolerância só se o atendimento já tiver sido iniciado. */
export function canOpenCallLink(
  event: AttendanceEventRef | null | undefined,
  canUpdate: boolean,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  now: Date = new Date()
): boolean {
  if (!canUpdate || !event) return false
  // Só no agendamento vigente (`marcado`); reagendado é o antigo.
  if (event.status !== 'marcado') return false
  if (ATTENDANCE_TIME_WINDOW_DISABLED || hasAttendanceStarted(event)) return true
  if (isAppointmentPastForAttendance(event.scheduled_at, toleranceMinutes, now)) return false
  return true
}

type TriageEditableRef = {
  status: string
  appointment?: { scheduled_at?: string | null } | null
}

/** Triagem só é editável enquanto pendente e dentro da janela do agendamento. */
export function canPerformTriage(
  triage: TriageEditableRef | null | undefined,
  canUpdate: boolean,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  now: Date = new Date()
): boolean {
  if (!canUpdate || !triage) return false
  if (triage.status !== 'pendente') return false
  const scheduledAt = triage.appointment?.scheduled_at
  if (scheduledAt && isAppointmentPastForAttendance(scheduledAt, toleranceMinutes, now)) {
    return false
  }
  return true
}
